/**
 * WatchPage — proactive site monitor (Track A, A3).
 *
 * Two surfaces:
 *   - Watchlist — sites the api-shim worker periodically snapshots.
 *     Admins add/remove sites and can trigger an immediate check.
 *   - Regression feed — events raised when a watched site degrades.
 *     Each event carries an auto-run AI triage verdict.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  Plus,
  Radar,
  RefreshCw,
  Siren,
  Stethoscope,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { SitePicker } from "@/components/app/site-picker";
import { cn } from "@/lib/cn";
import { useAuth } from "@/stores/auth";
import { useWatch, type WatchEvent, type WatchSeverity } from "@/stores/watch";
import type { PickedSite, SiteVendor } from "@/components/app/site-picker";

/* ---------- presentation ---------- */

const SEVERITY_META: Record<
  WatchSeverity,
  { label: string; badge: BadgeProps["variant"]; icon: typeof Siren; dot: string; band: string }
> = {
  severe: {
    label: "Severe",
    badge: "err",
    icon: Siren,
    dot: "bg-rose",
    band: "border-rose/25 bg-rose/10 text-rose",
  },
  warning: {
    label: "Warning",
    badge: "warn",
    icon: AlertTriangle,
    dot: "bg-peach",
    band: "border-peach/25 bg-peach/10 text-peach",
  },
};

const VERDICT_BADGE: Record<string, BadgeProps["variant"]> = {
  escalate: "err",
  support_resolvable: "ok",
};

function timeAgo(iso: string): string {
  const t = Date.parse(iso.replace(" ", "T") + "Z");
  if (Number.isNaN(t)) return iso;
  const secs = Math.max(0, (Date.now() - t) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/* ---------- regression event card ---------- */

function EventCard({ event, onAck }: { event: WatchEvent; onAck: (id: string) => void }) {
  const meta = SEVERITY_META[event.severity] ?? SEVERITY_META.warning;
  const Icon = meta.icon;
  const regressions = event.detail?.regressions ?? [];
  const verdict = event.detail?.verdict ?? null;
  const acked = !!event.ackedAt;

  return (
    <Card className={cn(acked && "opacity-60")}>
      <div className={cn("flex flex-wrap items-center gap-3 border-b px-5 py-3", meta.band)}>
        <Icon size={16} className="shrink-0" />
        <span className="text-sm font-bold tracking-tight">{meta.label} regression</span>
        {verdict ? (
          <Badge variant={VERDICT_BADGE[verdict] ?? "neutral"} size="md">
            AI:{" "}
            {verdict === "escalate"
              ? "escalate"
              : verdict === "support_resolvable"
                ? "support-resolvable"
                : verdict}
          </Badge>
        ) : null}
        <span className="ml-auto font-mono text-[10.5px] text-fg-mute">
          {timeAgo(event.detectedAt)}
        </span>
      </div>
      <CardContent className="space-y-3">
        <p className="m-0 text-sm text-fg">{event.summary}</p>

        {regressions.length > 0 ? (
          <div>
            <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
              Devices that went down ({regressions.length})
            </div>
            <ul className="m-0 space-y-1 p-0">
              {regressions.slice(0, 12).map((r) => (
                <li key={r.serial} className="flex items-center gap-2 text-xs text-fg-dim">
                  <span
                    aria-hidden="true"
                    className={cn("h-1.5 w-1.5 shrink-0 rounded-full", meta.dot)}
                  />
                  <span className="font-medium text-fg">{r.name || r.serial}</span>
                  <span className="text-fg-mute">{r.model}</span>
                  <span className="font-mono text-[10px] text-fg-mute">
                    {r.from} → {r.to}
                  </span>
                </li>
              ))}
              {regressions.length > 12 ? (
                <li className="pl-3.5 text-[11px] text-fg-mute">
                  +{regressions.length - 12} more
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}

        <div className="flex items-center gap-2 pt-1">
          <Button asChild size="sm" variant="ghost">
            <Link
              to={
                `/diagnose?site=${encodeURIComponent(event.siteSlug)}` +
                `&vendor=${encodeURIComponent(event.detail?.vendor || "meraki")}`
              }
            >
              <Stethoscope size={13} />
              {event.diagnosisId ? "View AI triage" : "Diagnose"}
            </Link>
          </Button>
          {acked ? (
            <span className="flex items-center gap-1.5 text-xs text-mint">
              <CheckCircle2 size={13} />
              Acknowledged
            </span>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => onAck(event.id)}>
              <CheckCircle2 size={13} />
              Acknowledge
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------- page ---------- */

export function WatchPage() {
  const user = useAuth((s) => s.user);
  const isAdmin = (user?.roles ?? []).includes("admin");

  const sites = useWatch((s) => s.sites);
  const events = useWatch((s) => s.events);
  const status = useWatch((s) => s.status);
  const error = useWatch((s) => s.error);
  const running = useWatch((s) => s.running);
  const loadAll = useWatch((s) => s.loadAll);
  const addSite = useWatch((s) => s.addSite);
  const removeSite = useWatch((s) => s.removeSite);
  const setEnabled = useWatch((s) => s.setEnabled);
  const ackEvent = useWatch((s) => s.ackEvent);
  const runNow = useWatch((s) => s.runNow);

  const [toAdd, setToAdd] = useState<PickedSite | null>(null);
  const [addVendor, setAddVendor] = useState<SiteVendor>("meraki");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      const aAck = a.ackedAt ? 1 : 0;
      const bAck = b.ackedAt ? 1 : 0;
      if (aAck !== bAck) return aAck - bAck;
      return b.detectedAt.localeCompare(a.detectedAt);
    });
  }, [events]);

  const unacked = events.filter((e) => !e.ackedAt).length;

  function onAddVendorChange(v: SiteVendor) {
    if (v === addVendor) return;
    setAddVendor(v);
    setToAdd(null);
  }

  async function onAdd() {
    if (!toAdd || adding) return;
    setAdding(true);
    const ok = await addSite(toAdd.slug, toAdd.vendor);
    setAdding(false);
    if (ok) {
      toast.success(`${toAdd.code} added to the watchlist`);
      setToAdd(null);
    } else {
      toast.error(useWatch.getState().error || "Could not add site");
    }
  }

  async function onRunNow() {
    const res = await runNow();
    if (res) {
      toast.success(
        `Checked ${res.checked} site${res.checked === 1 ? "" : "s"} — ` +
          `${res.events} regression${res.events === 1 ? "" : "s"} found`,
      );
    } else {
      toast.error(useWatch.getState().error || "Watch run failed");
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          AI
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Site Watch</span>
      </div>

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 flex items-center gap-2.5 text-3xl font-extrabold tracking-tight text-aurora">
            <Radar size={26} className="text-violet" />
            Site Watch
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Proactive monitoring. Opus snapshots each watched site every 30 minutes and
            raises a regression event when devices drop offline since the last check —
            with an AI triage verdict auto-attached, so a problem is caught and assessed
            before anyone calls support.
          </p>
        </div>
        {isAdmin ? (
          <Button variant="ghost" onClick={() => void onRunNow()} disabled={running}>
            {running ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Checking…
              </>
            ) : (
              <>
                <RefreshCw size={14} />
                Run check now
              </>
            )}
          </Button>
        ) : null}
      </div>

      {error ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-rose/30 bg-rose/10 px-3.5 py-2.5 text-sm text-rose"
        >
          <AlertTriangle size={16} />
          <span>{error}</span>
        </output>
      ) : null}

      {/* Watchlist */}
      <Card>
        <div className="flex items-center justify-between border-b border-line px-5 py-3">
          <span className="text-sm font-bold tracking-tight text-fg">
            Watchlist
            <span className="ml-2 font-mono text-[11px] font-normal text-fg-mute">
              {sites.length} site{sites.length === 1 ? "" : "s"}
            </span>
          </span>
        </div>
        <CardContent className="space-y-3">
          {isAdmin ? (
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-[260px] flex-1">
                <SitePicker
                  value={toAdd}
                  onPick={setToAdd}
                  vendor={addVendor}
                  onVendorChange={onAddVendorChange}
                  vendors={["meraki", "aruba", "cli"]}
                  placeholder="Select a site to watch…"
                />
              </div>
              <Button onClick={() => void onAdd()} disabled={!toAdd || adding}>
                {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Add to watchlist
              </Button>
            </div>
          ) : null}

          {sites.length === 0 ? (
            <p className="m-0 py-4 text-center text-xs text-fg-mute">
              {status === "loading"
                ? "Loading…"
                : isAdmin
                  ? "No sites watched yet — add one above."
                  : "No sites are being watched."}
            </p>
          ) : (
            <ul className="m-0 divide-y divide-line/60 p-0">
              {sites.map((s) => (
                <li key={s.siteSlug} className="flex items-center gap-3 py-2.5">
                  <span
                    aria-hidden="true"
                    className={cn(
                      "h-2 w-2 shrink-0 rounded-full",
                      s.enabled ? "bg-mint" : "bg-line-strong",
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-fg">
                      {s.siteName || s.siteSlug}
                    </div>
                    <div className="font-mono text-[10px] text-fg-mute">
                      {s.siteSlug} · added by {s.addedBy}
                    </div>
                  </div>
                  <Badge variant={s.vendor === "aruba" ? "info" : "neutral"} size="sm">
                    {s.vendor}
                  </Badge>
                  {!s.enabled ? (
                    <Badge variant="neutral" size="sm">
                      paused
                    </Badge>
                  ) : null}
                  {isAdmin ? (
                    <>
                      <button
                        type="button"
                        onClick={() => void setEnabled(s.siteSlug, !s.enabled)}
                        aria-label={s.enabled ? "Pause watching" : "Resume watching"}
                        title={s.enabled ? "Pause watching" : "Resume watching"}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-fg-mute hover:bg-surface-2 hover:text-fg"
                      >
                        {s.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => void removeSite(s.siteSlug)}
                        aria-label="Remove from watchlist"
                        title="Remove from watchlist"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-fg-mute hover:bg-rose/10 hover:text-rose"
                      >
                        <Trash2 size={14} />
                      </button>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Regression feed */}
      <div>
        <div className="mb-3 flex items-center gap-2">
          <h2 className="m-0 text-sm font-bold tracking-tight text-fg">Regression events</h2>
          {unacked > 0 ? (
            <Badge variant="err" size="sm">
              {unacked} unacknowledged
            </Badge>
          ) : null}
        </div>
        {sortedEvents.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
              <CheckCircle2 size={26} className="text-mint" />
              <div className="text-sm font-semibold text-fg">No regressions detected</div>
              <div className="max-w-[380px] text-xs text-fg-mute">
                Every watched site has held steady since its last check. New regressions
                appear here automatically — and fire the Teams webhook.
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="flex flex-col gap-4">
            {sortedEvents.map((e) => (
              <EventCard key={e.id} event={e} onAck={(id) => void ackEvent(id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
